// Suivi.jsx
import React, { useState, useEffect } from "react";

import { useEtatGlobal } from "../EtatGlobal.js";
import SuiviBilan from "./Annexe/SuiviBilan";
import SuiviCFE from "./Annexe/SuiviCFE";
import SuiviIS from "./Annexe/SuiviIS";
import SuiviTVAMensuel from "./Annexe/SuiviTVAMensuel";
import SuiviTVARSI from "./Annexe/SuiviTVARSI";
import SuiviJuridiqueAGO from "./Annexe/SuiviJuridiqueAGO";
import SuiviIR from "./Annexe/SuiviIR";
import SuiviTNS from "./Annexe/SuiviTNS";
import JuridiquePonctuelle from "./Annexe/JuridiquePonctuelle";
import SuiviCompta from "./Annexe/SuiviCompta";
import SuiviSocial from "./Annexe/SuiviSocial";
import SuiviTraitementsComptables from "./Annexe/SuiviTraitementsComptables";

// Groupes du sous-menu sidebar "Suivi Prod" (voir Home.js) — chaque groupe
// n'affiche sa barre d'onglets interne que pour lui-même, jamais les autres
// groupes en même temps (avant : les 4 groupes s'affichaient tous ensemble
// sous le nom "Vue générale", ce qui n'existe plus).
const GROUPES_SUIVI = {
  compta: ["Compta", "Bilan", "IS", "TVAMensuel", "TVARSI", "CFE"],
  ir: ["IR"],
  tns: ["TNS"],
  social: ["Social"],
  juridique: ["JuridiquePonctuelle", "JuridiqueAGO"],
  traitements: ["TraitementsComptables"],
};

function groupeDe(selectedDiv) {
  return (
    Object.entries(GROUPES_SUIVI).find(([, items]) =>
      items.includes(selectedDiv),
    )?.[0] || null
  );
}

// Titre/sous-titre par groupe (voir claude-design/production.jsx —
// SECTION_TITLE/SECTION_SUB) : avant, tous les groupes affichaient le même
// "Suivi de Production / Vue d'ensemble du cabinet" générique.
const GROUPE_TITRE = {
  compta: "Compta / fiscal",
  ir: "Suivi IR",
  tns: "Suivi TNS",
  social: "Suivi Social",
  juridique: "Juridique",
};

const GROUPE_SOUS_TITRE = {
  compta: "Suivi de production comptable & fiscale",
  ir: "Impôt sur le revenu",
  tns: "Déclarations & paiements des cotisations sociales des indépendants",
  social: "Paie & déclarations sociales",
  juridique: "Missions juridiques & AGO",
};

function Suivi({ initialTab = "Compta", navKey }) {
  const [selectedDiv, setSelectedDiv] = useState(initialTab);

  // Suivi ne se démonte pas en changeant de sous-item du sous-menu sidebar
  // (on reste sur currentView === "Suivi"), donc initialTab seul ne suffit
  // pas à resynchroniser selectedDiv — navKey s'incrémente à chaque clic du
  // menu et force la resynchronisation, même quand initialTab ne change pas
  // de valeur (même pattern que le fix navKey d'Admin.js).
  useEffect(() => {
    setSelectedDiv(initialTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navKey]);

  const {
    suiviBilanData,
    loadingBilan,
    suiviCFEData,
    loadingCFE,
    suiviSIData,
    loadingSI,
    suiviTVAMensuelData,
    loadingTVAMensuel,
    suiviSocialData,
    loadingSocial,
    suiviTVARSIData,
    loadingTVARSI,
    suiviJuridiqueData,
    loadingJuridique,
    suiviIRData,
    loadingIR,
    fetchSuiviIR,
    suiviTNSData,
    loadingTNS,
    fetchSuiviTNS,
    suiviComptaData,
    loadingCompta,
    fetchSuiviCompta,
    fetchSuiviTVAMensuel,
    fetchSuiviSocial,
    fetchSuiviBilan,
    fetchSuiviJuridique,
    fetchSuiviCFE,
    fetchSuiviTVARSI,
    fetchSuiviIS,
  } = useEtatGlobal();

  // Uniquement les groupes à plusieurs interfaces (compta, juridique) : IR/
  // TNS/Social sont des interfaces uniques, sans barre d'onglets à afficher.
  const navItems = [
    { name: "Compta", label: "Compta", group: "compta" },
    { name: "Bilan", label: "Bilan", group: "compta" },
    { name: "IS", label: "IS", group: "compta" },
    { name: "TVAMensuel", label: "TVA MENSUEL", group: "compta" },
    { name: "TVARSI", label: "TVA RSI", group: "compta" },
    { name: "CFE", label: "CFE", group: "compta" },

    { name: "JuridiquePonctuelle", label: "Juridique", group: "juridique" },
    { name: "JuridiqueAGO", label: "Juridique AGO", group: "juridique" },
  ];

  const renderSelectedComponent = () => {
    switch (selectedDiv) {
      case "Bilan":
        return (
          <SuiviBilan
            suiviBilanData={suiviBilanData}
            loading={loadingBilan}
            refetch={fetchSuiviBilan}
            refetchAGO={fetchSuiviJuridique}
          />
        );
      case "CFE":
        return (
          <SuiviCFE
            suiviCFEData={suiviCFEData}
            loading={loadingCFE}
            refetch={fetchSuiviCFE}
          />
        );
      case "IS":
        return (
          <SuiviIS
            suivi={suiviSIData}
            loading={loadingSI}
            refetch={fetchSuiviIS}
          />
        );
      case "TVAMensuel":
        return (
          <SuiviTVAMensuel
            suivi={suiviTVAMensuelData}
            loading={loadingTVAMensuel}
            refetch={fetchSuiviTVAMensuel}
          />
        );
      case "Social":
        return (
          <SuiviSocial
            suivi={suiviSocialData}
            loading={loadingSocial}
            refetch={fetchSuiviSocial}
          />
        );
      case "TVARSI":
        return (
          <SuiviTVARSI
            suivi={suiviTVARSIData}
            loading={loadingTVARSI}
            refetch={fetchSuiviTVARSI}
          />
        );
      case "JuridiqueAGO":
        return (
          <SuiviJuridiqueAGO
            suiviJuridiqueData={suiviJuridiqueData}
            loading={loadingJuridique}
            refetch={fetchSuiviJuridique}
          />
        );
      case "IR":
        return <SuiviIR suivi={suiviIRData} loading={loadingIR} refetch={fetchSuiviIR} />;
      case "JuridiquePonctuelle":
        return <JuridiquePonctuelle />;
      case "Compta":
        return (
          <SuiviCompta
            suiviComptaData={suiviComptaData}
            loading={loadingCompta}
            refetch={fetchSuiviCompta}
          />
        );
      case "TraitementsComptables":
        return <SuiviTraitementsComptables />;
      case "TNS":
        return <SuiviTNS suivi={suiviTNSData} loading={loadingTNS} refetch={fetchSuiviTNS} />;
      default:
        return null;
    }
  };

  const TabButton = ({ item }) => {
    const isActive = selectedDiv === item.name;

    return (
      <button
        onClick={() => setSelectedDiv(item.name)}
        className={`
          px-3 py-1 rounded-md transition font-semibold
          ${
            isActive
              ? "bg-[#6e1a36] text-white"
              : "hover:bg-black/5 text-slate-700"
          }
        `}
        style={{ fontSize: "0.75rem" }}
      >
        {item.label}
      </button>
    );
  };

  const renderGroup = (group, styles) => (
    <div
      className={`flex flex-wrap gap-1 px-2 py-1.5 rounded-lg ${styles}`}
    >
      {navItems
        .filter((item) => item.group === group)
        .map((item) => (
          <TabButton key={item.name} item={item} />
        ))}
    </div>
  );

  const groupeActuel = groupeDe(selectedDiv);
  const isTraitementsComptables = groupeActuel === "traitements";
  // Barre d'onglets interne : seulement pour basculer entre les interfaces
  // d'un même groupe (compta : Compta/Bilan/IS/TVA Mensuel/TVA RSI/CFE ;
  // juridique : Juridique/Juridique AGO) — IR/TNS/Social n'ont qu'une seule
  // interface chacun, rien à afficher pour eux.
  const afficherOnglets = groupeActuel === "compta" || groupeActuel === "juridique";

  return (
    <div>
      {!isTraitementsComptables && (
        <div className="relative flex items-center mb-5 flex-wrap">
          <div className="mb-4">
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold text-gray-900 m-0">
                {GROUPE_TITRE[groupeActuel] || "Suivi de Production"}
              </h1>
              {/* Nombre d'indépendants suivis — même pastille que Portefeuille. */}
              {groupeActuel === "tns" && suiviTNSData?.length > 0 && (
                <div className="bg-[#9B1B5D]/80 text-white/90 text-xs px-1.5 py-1.5 rounded-full font-medium flex items-center justify-center leading-none">
                  {suiviTNSData.length}
                </div>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-1">
              {GROUPE_SOUS_TITRE[groupeActuel] || "Vue d'ensemble du cabinet"}
            </p>
          </div>
          {/* Centrée par rapport à toute la ligne (pas seulement l'espace
              restant à droite du titre) — indépendante de la largeur du titre. */}
          {afficherOnglets && (
            <div className="absolute left-1/2 -translate-x-1/2 p-2 flex items-center gap-2 flex-wrap">
              {groupeActuel === "compta" &&
                renderGroup("compta", "bg-[#f5ece4] border-[#e8d5c4]")}
              {groupeActuel === "juridique" &&
                renderGroup("juridique", "bg-blue-50 border-blue-200")}
            </div>
          )}
        </div>
      )}

      <div>{renderSelectedComponent()}</div>
    </div>
  );
}

export default Suivi;
